import { resolveLanguageKey } from "@packages/core/language/language.ts";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo } from "react";
import type { Selection } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { TENURE_BAND_LABELS, TENURE_BUCKET_KEYS, type TenureBucketKey } from "#shared/reviewee-filters/tenureBuckets.ts";
import { Button } from "@/components/ui/button.tsx";
import { ListBox, ListBoxItem } from "@/components/ui/list-box.tsx";
import { Radio, RadioField, RadioGroup } from "@/components/ui/radio.tsx";
import { reviewCyclesI18NextInstanceKey } from "../../i18n/i18next.ts";
import { FilterPanelBody } from "./FilterPanelBody.tsx";
import { FilterPanelHeader } from "./FilterPanelHeader.tsx";

export interface TenureFilterPanelProps {
    selectedBuckets: TenureBucketKey[];
    onSelectionChange: (buckets: TenureBucketKey[]) => void;
    onBack: () => void;
    selectionMode?: "single" | "multiple";
    maxSelections?: number;
}

function toBucketKeys(values: Iterable<string>) {
    return [...values].flatMap(value => TENURE_BUCKET_KEYS.filter(key => key === value));
}

export function TenureFilterPanel({ selectedBuckets, onSelectionChange, onBack, selectionMode = "single", maxSelections }: TenureFilterPanelProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("TenureFilterPanel", { i18n: i18nextInstance });
    const language = resolveLanguageKey(i18nextInstance.language);

    const isAtCap = maxSelections !== undefined && selectedBuckets.length >= maxSelections;

    const handleRadioChange = useCallback((value: string) => onSelectionChange(toBucketKeys([value])), [onSelectionChange]);
    const handleClear = useCallback(() => onSelectionChange([]), [onSelectionChange]);

    const handleListChange = useCallback(
        (keys: Selection) => {
            const next = toBucketKeys(keys === "all" ? TENURE_BUCKET_KEYS : [...keys].map(String));

            onSelectionChange(maxSelections === undefined ? next : next.slice(0, maxSelections));
        },
        [onSelectionChange, maxSelections]
    );

    const bands = useMemo(() => TENURE_BUCKET_KEYS.map(bucket => ({ id: bucket, label: TENURE_BAND_LABELS[bucket]?.[language] ?? bucket })), [language]);

    return (
        <FilterPanelBody className="w-72">
            <FilterPanelHeader title={t("title")} onBack={onBack} backLabel={t("back")} />
            {selectionMode === "multiple" ? (
                <ListBox aria-label={t("title")} selectionMode="multiple" selectedKeys={selectedBuckets} onSelectionChange={handleListChange} escapeKeyBehavior="none" items={bands} className="max-h-64 min-h-0 border-0">
                    {band => (
                        <ListBoxItem id={band.id} textValue={band.label} isDisabled={isAtCap && !selectedBuckets.includes(band.id)}>
                            {band.label}
                        </ListBoxItem>
                    )}
                </ListBox>
            ) : (
                <>
                    <RadioGroup aria-label={t("title")} value={selectedBuckets[0] ?? null} onChange={handleRadioChange}>
                        {bands.map(band => (
                            <RadioField key={band.id} value={band.id}>
                                <Radio>{band.label}</Radio>
                            </RadioField>
                        ))}
                    </RadioGroup>
                    {selectedBuckets.length > 0 && (
                        <Button intent="plain" size="xs" onPress={handleClear} className="text-muted-fg hover:text-fg self-start">
                            {t("clear")}
                        </Button>
                    )}
                </>
            )}
        </FilterPanelBody>
    );
}
